/**
 * Seletor de aparelhos e emissao de claim code.
 *
 * Responsabilidade: mostrar todos os aparelhos do tenant, deixar escolher qual
 * o resto da tela esta olhando, e emitir o codigo de enrollment.
 *
 * NAO e responsabilidade deste arquivo: enrolar. O enrollment acontece no
 * APARELHO — o usuario cola o codigo no painel local, o firmware gera o par de
 * chaves e o CSR e chama `POST /v1/enroll` (contrato §2). Esta tela so emite o
 * codigo; a chave privada nunca sai do aparelho e esta PWA nunca a ve.
 *
 * ## Trilho, e nao lista escondida
 *
 * O painel local do `gfc` esconde os outros controladores atras de um botao
 * porque la o normal e haver um. Aqui o normal e haver mais de um — e a razao
 * de existir uma PWA hospedada. Entao os aparelhos ficam sempre visiveis, com
 * a presenca de cada um a vista: e a primeira coisa que alguem com tres
 * barracas quer saber ao abrir o painel.
 */

import * as acoes from "./../acoes";
import * as api from "./../api";
import * as store from "./../store";
import type { AparelhoNoSnapshot } from "./../tipos";
import { dataHora, el, trocar, type Painel } from "./../ui";

export function montar(): Painel {
  const trilho = el("div", { class: "trilho" });
  const dialogo = el("dialog", { class: "dialogo" });

  const adicionar = el("button", {
    type: "button",
    class: "aparelho novo",
    text: "+ aparelho",
    onclick: () => void emitirCodigo(dialogo),
  });

  const raiz = el("section", { class: "aparelhos" }, trilho, dialogo);

  function atualizar(): void {
    const estado = store.obter();
    trocar(
      trilho,
      ...estado.aparelhos.map((a) => cartao(a, a.device.id === estado.selecionado)),
      adicionar,
    );
  }

  return { raiz, atualizar };
}

function cartao(alvo: AparelhoNoSnapshot, selecionado: boolean): HTMLElement {
  const { device } = alvo;
  const nome = device.dev_name !== "" ? device.dev_name : device.mac;
  return el(
    "button",
    {
      type: "button",
      class: `aparelho ${selecionado ? "selecionado" : ""}`,
      "aria-pressed": String(selecionado),
      // So muda a selecao: quem busca uploads e comandos do aparelho novo e o
      // `acoes.acompanharSelecao()`, um lugar so para todos os caminhos.
      onclick: () => store.selecionar(device.id),
    },
    el(
      "span",
      { class: "aparelho-nome" },
      el("span", { class: `ponto ${device.online ? "ligada" : ""}` }),
      nome,
    ),
    el("span", {
      class: "aparelho-meta",
      text: device.online ? "online" : `visto ${dataHora(device.last_seen)}`,
    }),
  );
}

/**
 * Emite o claim code e mostra o passo a passo.
 *
 * TTL de 10 min e uso unico (contrato §2): por isso a tela mostra o horario de
 * expiracao em vez de deixar o codigo la parecendo valido para sempre.
 */
async function emitirCodigo(dialogo: HTMLDialogElement): Promise<void> {
  trocar(dialogo, el("p", { class: "hint", text: "Gerando código…" }));
  dialogo.showModal();

  const emitido = await acoes.executar("gerar código", () => api.criarClaimCode());
  if (emitido === null) {
    dialogo.close();
    return;
  }

  trocar(
    dialogo,
    el("h2", { text: "Adicionar aparelho" }),
    el("p", { class: "codigo", text: emitido.code }),
    el("ol", { class: "passos" },
      el("li", { text: "Abra o painel local do controlador (grow.local)." }),
      el("li", { text: "Ligue a opção Nuvem." }),
      el("li", { text: "Cole este código." }),
    ),
    el("p", {
      class: "hint",
      text: `Vale até ${dataHora(emitido.expires_at)}, uma vez só.`,
    }),
    el("button", {
      type: "button",
      class: "primary",
      text: "Fechar",
      onclick: () => dialogo.close(),
    }),
  );
}
